import {
  Box,
  Button,
  FormControl,
  Grid,
  IconButton,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import makeStyles from "@mui/styles/makeStyles";
import * as React from "react";
import { TreeView } from "@mui/x-tree-view/TreeView";
import { KeyValue, KeyValueObject, Query } from "../../interfaces";
import Edit from "@mui/icons-material/Edit";
import ClearIcon from "@mui/icons-material/Clear";
import CheckIcon from "@mui/icons-material/Check";
import { apiPost, fetchData } from "../../common/services/ApiClient";
import { ApiBaseUrl } from "../../config";
import LoadingIndicator from "../LoadingIndicator";
import {
  reduceObj,
  getCookiebyName,
  chunkArray,
} from "../../common/services/Utilities";
import RenderCustomTree from "./RenderCustomTree";
import ModalDialog from "../ModalDialog";
import SaveNameInput from "../PeerGroup/SaveNameInput";
import { useHistory } from "react-router-dom";
import LookupVariable from "../../common/components/LookupVariable";

interface Props {
  appState: Query;
  data: KeyValue[] | undefined;
  updateGenerateVars: (values: string[]) => void;
  updateReportType: (reportStructure: KeyValue[]) => void;
  customVariant?: boolean;
  returnPath?: string;
  banner?: string;
}
interface RenderTree {
  variable_ytd: string;
  variable_ytdr: string;
  variable_qtly: string;
  variable_qtlyr: string;
  label: string;
  unit_type: string;
  fields: RenderTree[];
  sub_fields?: RenderTree[];
}

const useStyles = makeStyles(theme => ({
  singleTextInput: {
    "marginTop": 0,
    "width": "100%",
    "marginRight": "1rem",
    "& input": {
      height: 40,
    },
  },
  loadingSpinner: { textAlign: "center", marginTop: "2rem" },
  modalHeaderText: {
    paddingBottom: 0,
  },
  variableErrors: {
    "& h3": { padding: 0 },
  },
  textArea: {
    "marginBottom": "1rem",
    "& textarea": {
      textTransform: "uppercase",
    },
  },
  fieldErrors: {
    "wordBreak": "break-word",
    "padding": ".25rem",
    "margin": ".25rem 0",
    "borderRadius": 5,
    "& .fieldName p": {
      padding: ".5rem",
    },
    "&:hover": {
      backgroundColor: "#D9E8F6",
    },
    "& .modifyButton": {
      "padding": ".5rem",
      "float": "right",
      "&:hover": { backgroundColor: "#ffffff" },
    },
    "&:last-child": {
      marginBottom: "1rem",
    },
  },
  manuallyEditButtons: {
    "& .MuiButton-root.MuiButton-containedSecondary": {
      margin: 0,
    },
  },
  editField: {
    "& input": {
      textTransform: "uppercase",
    },
    "padding": ".25rem",
    "margin": ".25rem 0",
  },
}));
export default function FinancialCustomReportStructure({
  appState,
  data,
  updateReportType,
  updateGenerateVars,
  customVariant,
  returnPath,
  banner,
}: Props) {
  const reportStructure: RenderTree[] = appState.ui.reportStructure;
  const classes = useStyles();
  const history = useHistory();

  const ignoredVariables = React.useMemo(
    () => ["NAMEFULL", "TOTAL", "", " ", "  ", "undefined"],
    [],
  );

  const [selectedCheckboxes, setSelectedCheckboxes] = React.useState<string[]>(
    appState.ui.customVariables ? appState.ui.customVariables : [],
  );

  const [checkboxState, setCheckboxState] = React.useState<KeyValue>([]);
  const [incomeBasis, setIncomeBasis] = React.useState(appState.ui.incomeBasis);
  const [removeValues, setRemoveValues] = React.useState(false);
  const [subFields, setSubfields] = React.useState<KeyValueObject[]>([]);
  const [loadVariables, setLoadVariables] = React.useState(false);
  const [editMany, setEditMany] = React.useState(false);
  const [definitionValues, setDefinitionValues] = React.useState<KeyValue>({
    longDesc: {},
    unitType: {},
    incomeBasis: {},
    footNote: {},
    ftnKey: {},
    demographicOnly: {},
  });
  const [openSaveModal, setOpenSaveModal] = React.useState(false);
  const [previousStructure, setPreviousStructure] = React.useState([]);
  const [errorFields, setErrorFields] = React.useState<string[]>([]);
  const [editError, setEditError] = React.useState("");
  const [tempNewErrorState, setTempNewErrorState] = React.useState<string[]>(
    errorFields,
  );
  const [displayNoNameError, setDisplayNoNameError] = React.useState(false);
  const [customReportName, setCustomReportName] = React.useState("");
  const [noUnitVariables, setNoUnitVariables] = React.useState<string[]>([]);
  const [
    previousSelectedCheckoxes,
    setPreviousSelectedCheckboxes,
  ] = React.useState<string[]>([]);
  const [editId, setEditId] = React.useState("");
  const [editTitle, setEditTitle] = React.useState("");

  const displayDuplicateNameError: boolean =
    appState.ui.savedReportNames &&
    customReportName !== editTitle &&
    appState.ui.savedReportNames.some(
      (name: string) => name === customReportName,
    );

  const modalInputField = (
    <SaveNameInput
      value={customReportName}
      onChange={setCustomReportName}
      header="Name This Custom Report"
      errors={
        (!customReportName && displayNoNameError) || displayDuplicateNameError
      }
      errorText={
        displayNoNameError
          ? "Please provide a report name."
          : displayDuplicateNameError
          ? "Name already exists."
          : ""
      }
    />
  );
  const [selectedValues, setSelectedValues] = React.useState("");
  const filteredVariables = selectedCheckboxes.filter(
    (item: string) =>
      !ignoredVariables.includes(item) &&
      Object.keys(checkboxState).includes(item),
  );

  const user = getCookiebyName("id_token");
  React.useEffect(() => {
    const savedReportId = appState.ui.savedReportId;
    if (savedReportId && savedReportId !== editId) {
      (async () => {
        await fetchData(
          `${ApiBaseUrl()}/report_types?filters=_id:"${savedReportId}"&user=${getCookiebyName(
            "uuid",
          )} `,
        )
          .then(response => {
            const report = response.data[0];
            const savedStateVariables = report.structure[0].fields
              .map((variable: KeyValue) => variable.variable_ytd)
              .filter((value: string) => !ignoredVariables.includes(value));
            setSelectedCheckboxes(savedStateVariables);
            setEditId(report.ID);
            setEditTitle(report.title);
            setCustomReportName(report.title);
          })
          .catch(error => {
            console.error(`${error.name} : ${error.message}`);
          });
      })();
    }
  }, [appState, editId, ignoredVariables, user]);

  const generateSaveStructure = React.useMemo(
    () => [
      {
        label: customReportName,

        fields: [
          {
            variable_ytd: "NAMEFULL",
            label: "Institution Name at Time of Reporting",
          },
          {
            variable_ytd: "TOTAL",
            label: "Number of Institutions Reporting",
          },
          ...filteredVariables.map((variable: string) => {
            let label = variable;
            if (definitionValues.longDesc[variable]) {
              label = definitionValues.longDesc[variable];
              if (definitionValues.incomeBasis[variable]) {
                label = `${label} (${definitionValues.incomeBasis[variable]})`;
              }
              if (
                definitionValues.unitType[variable] &&
                !noUnitVariables.includes(variable)
              ) {
                label = `${label} (${definitionValues.unitType[variable]})`;
              }
            }

            return {
              variable_ytd: variable,
              label: label,
              unitType: definitionValues.unitType[variable]
                ? definitionValues.unitType[variable]
                : "",
              no_unit: noUnitVariables.includes(variable),
              ftnKey: definitionValues.ftnKey[variable],
              demographicOnly: definitionValues.demographicOnly[variable],
              footNote:
                definitionValues.ftnKey[variable] &&
                `${definitionValues.ftnKey[variable]} ${definitionValues.footNote[variable]}`,
            };
          }),
        ],
      },
    ],
    [customReportName, definitionValues, filteredVariables, noUnitVariables],
  );
  const saveReportFetch = () => {
    if (!customReportName) {
      setDisplayNoNameError(true);
    } else {
      setDisplayNoNameError(false);
      return fetch(
        `${ApiBaseUrl()}/report_types`,
        apiPost(
          {
            title: customReportName,
            created_by: getCookiebyName("uuid"),
            is_custom: true,
            containsDemographics: generateSaveStructure[0].fields.some(
              (val: KeyValue) => val.demographicOnly === "TRUE",
            ),
            structure: generateSaveStructure,
            ID: editId || `${getCookiebyName("uuid")}_${Date.now()}`,
          },
          { Authorization: `Bearer ${user}` },
        ),
      );
    }
  };

  const saveModal = (
    <ModalDialog
      title="Save Custom Report"
      display={openSaveModal}
      onExited={() => setCustomReportName("")}
      setDisplay={setOpenSaveModal}
      maxWidth="sm"
      noCloseXIcon
      onSubmit={() => {
        saveReportFetch();
        setOpenSaveModal(false);
      }}
      submitText="Save Custom Report"
      disabledSubmit={!customReportName}
    >
      {modalInputField}
    </ModalDialog>
  );

  React.useEffect(() => {
    const filteredSelectedCheckboxes = selectedCheckboxes.filter(
      (item: string) =>
        !ignoredVariables.includes(item) &&
        Object.keys(checkboxState).includes(item),
    );

    if (
      filteredSelectedCheckboxes.length > 0 &&
      JSON.stringify(previousSelectedCheckoxes) !==
        JSON.stringify(filteredSelectedCheckboxes)
    ) {
      setPreviousSelectedCheckboxes(filteredSelectedCheckboxes);

      const splitCheckboxes: any = chunkArray(filteredSelectedCheckboxes, 300);

      const requests = splitCheckboxes.map((checkBoxes: string[]) => {
        const requestBody: KeyValue = {
          filters: `VARIABLE:(${checkBoxes.join(" OR ")})`,
          offset: 0,
          limit: 2000,
        };
        const definitionsPostReq = apiPost(requestBody);
        return fetchData(`${ApiBaseUrl()}/definitions`, definitionsPostReq);
      });
      (async () => {
        await Promise.all(requests)
          .then(response => {
            const definitionArrays: KeyValue = {
              LONG_DESCRIPTION: [],
              UNIT_TYPE: [],
              INCOME_BASIS: [],
              FOOTNOTE: [],
              FTN_KEY: [],
              DEMOGRAPHIC_FLAG: [],
            };
            const combinedResponses = response.map(res => res.data).flat();

            combinedResponses.map((d: KeyValue) => {
              return Object.keys(definitionArrays).map((array: string) => {
                return definitionArrays[array].push({
                  key: d.VARIABLE,
                  value: d[array],
                });
              });
            });
            setDefinitionValues({
              longDesc: reduceObj(definitionArrays.LONG_DESCRIPTION),
              unitType: reduceObj(definitionArrays.UNIT_TYPE),
              incomeBasis: reduceObj(definitionArrays.INCOME_BASIS),
              footNote: reduceObj(definitionArrays.FOOTNOTE),
              ftnKey: reduceObj(definitionArrays.FTN_KEY),
              demographicOnly: reduceObj(definitionArrays.DEMOGRAPHIC_FLAG),
            });
          })
          .catch((error: Error) => {
            console.error(error);
          });
      })();
    }
  }, [
    checkboxState,
    ignoredVariables,
    previousSelectedCheckoxes,
    selectedCheckboxes,
  ]);

  React.useEffect(() => {
    if (subFields.length > 0) {
      const checkFields = reduceObj(subFields);
      // deletes empty or unnecessary variables
      ignoredVariables.forEach((val: string) => {
        delete checkFields[val];
      });

      // only sets the values to errorFields when the checkboxes are selected, otherwise it sets it to empty if the errorfields include the some subfields
      const errors: string[] = subFields.some((field: KeyValue) =>
        errorFields.includes(field.key),
      )
        ? []
        : errorFields;

      Object.keys(checkFields).forEach((field: string) => {
        if (!Object.keys(checkboxState).includes(field)) {
          if (!errors.includes(field)) {
            errors.push(field);
          }
          delete checkFields[field];
        }
      });
      setErrorFields(errors);
      const subFieldValues = subFields.map((field: KeyValue) => field.key);
      const checked = subFields[0].value;
      const allFalseObj = Object.keys(checkboxState).reduce(
        (checkbox: KeyValue, value: string) => {
          checkbox[value] = false;
          return checkbox;
        },
        {},
      );
      setCheckboxState(
        removeValues
          ? { ...allFalseObj, ...checkFields }
          : {
              ...checkboxState,
              ...checkFields,
            },
      );

      const selectedSubValues = subFieldValues.filter(
        (value: string, index: number) => {
          return (
            subFieldValues.indexOf(value) === index &&
            !ignoredVariables.includes(value)
          );
        },
      );
      const combinedArray = [...selectedCheckboxes, ...selectedSubValues];
      setSelectedCheckboxes(
        checked
          ? removeValues
            ? selectedSubValues
            : combinedArray.filter(
                (item: string, index: number) =>
                  combinedArray.indexOf(item) === index,
              )
          : selectedCheckboxes.filter((unchecked: string) => {
              return !subFieldValues.includes(unchecked);
            }),
      );
      setSubfields([]);
      setRemoveValues(false);
    }
  }, [
    checkboxState,
    errorFields,
    ignoredVariables,
    removeValues,
    selectedCheckboxes,
    subFields,
  ]);

  React.useEffect(() => {
    const issaved =
      appState.ui.savedReportId !== undefined
        ? selectedCheckboxes.length > 0
        : true;

    if (
      appState.ui.reportStructure &&
      JSON.stringify(appState.ui.reportStructure) !==
        JSON.stringify(previousStructure) &&
      issaved
    ) {
      const checkboxArray: KeyValueObject[] = [];
      const noUnitOptions: string[] = [];
      const pushCheckboxArray = (fields: KeyValue[]) => {
        fields.forEach((fieldVal: KeyValue) => {
          const fieldValOptions: KeyValueObject[] = [
            "variable_ytd",
            "variable_ytdr",
            "variable_qtly",
            "variable_qtlyr",
          ].map((field: string) => {
            if (fieldVal.no_unit && fieldVal[field]) {
              noUnitOptions.push(fieldVal[field]);
            }

            return {
              key: fieldVal[field],
              value:
                (appState.ui.customVariables &&
                  appState.ui.customVariables.includes(fieldVal[field])) ||
                selectedCheckboxes.includes(fieldVal[field])
                  ? true
                  : false,
            };
          });

          checkboxArray.push(...fieldValOptions);

          if (fieldVal.sub_fields) {
            pushCheckboxArray(fieldVal.sub_fields);
          }
        });
      };
      setNoUnitVariables(noUnitOptions);
      setPreviousStructure(appState.ui.reportStructure);
      appState.ui.reportStructure.map((structure: KeyValue) => {
        return pushCheckboxArray(structure.fields);
      });
      const checkBoxObj = reduceObj(checkboxArray);
      ignoredVariables.forEach((val: string) => {
        delete checkBoxObj[val];
      });
      setCheckboxState(checkBoxObj);
    }
  }, [
    appState.ui.customVariables,
    appState.ui.reportStructure,
    appState.ui.savedReportId,
    editId,
    ignoredVariables,
    previousStructure,
    selectedCheckboxes,
  ]);

  React.useEffect(() => {
    if (
      JSON.stringify(incomeBasis) !== JSON.stringify(appState.ui.incomeBasis)
    ) {
      setIncomeBasis(appState.ui.incomeBasis);
    }
  }, [incomeBasis, appState.ui.incomeBasis]);

  function removeErrorField(error: string) {
    setErrorFields(errorFields.filter((field: string) => field !== error));
    setSelectedCheckboxes(
      selectedCheckboxes.filter((field: string) => field !== error),
    );
  }
  function removeAllErrorFields(errors: string[]) {
    setErrorFields([]);

    setSelectedCheckboxes(
      selectedCheckboxes.filter((value: string) => !errors.includes(value)),
    );
  }

  function editSubmit(currentError: string, newError: string) {
    const errors: string[] = [];
    const newCheckboxState = selectedCheckboxes.map((value: string) =>
      value === currentError ? newError : value,
    );
    const newErrorState = errorFields.map((value: string) =>
      value === currentError ? newError : value,
    );

    // sets the editError to empty so the form exits without saving if the error is not updated, or if it's blank
    if (currentError === newError || !newError) {
      setEditError("");
    } else {
      newErrorState.forEach((field: string) => {
        if (!Object.keys(checkboxState).includes(field)) {
          errors.push(field);
        } else if (!selectedCheckboxes.includes(field)) {
          removeErrorField(field);
          setCheckboxState({
            ...checkboxState,
            [field]: true,
          });
        }
      });
      setErrorFields(errors);
      setTempNewErrorState(errors);
      setSelectedCheckboxes(
        newCheckboxState.filter(
          (value: string, index: number) =>
            newCheckboxState.indexOf(value) === index,
        ),
      );
    }
  }

  const errorFieldDisplay = errorFields.length > 0 && (
    <Box id="variableErrors" className={classes.variableErrors}>
      <Typography variant="h3" color="error">
        The following entries have errors:
      </Typography>
      {errorFields.map((error: string, index: number) => {
        return editError === error ? (
          <form
            key={index}
            onSubmit={(e: any) => {
              e.preventDefault();
              editSubmit(error, tempNewErrorState[index]);
            }}
          >
            <FormControl fullWidth>
              <TextField
                className={classes.editField}
                fullWidth
                id="modifyTextField"
                variant="outlined"
                onChange={(e: any) => {
                  const newErrorValues = errorFields.map((field: string) =>
                    field === error
                      ? e.target.value.toUpperCase()
                      : field.toUpperCase(),
                  );

                  setTempNewErrorState(newErrorValues);
                }}
                defaultValue={error}
                InputProps={{
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton
                        id="saveEdit"
                        type="submit"
                        title="Save Edit"
                        size="large"
                      >
                        <CheckIcon color="primary" />
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </FormControl>
          </form>
        ) : (
          <Box key={index}>
            <Grid
              container
              id={`error-${error}`}
              className={classes.fieldErrors}
            >
              <Grid item xs={8} className="fieldName">
                <Typography color="error">{error}</Typography>
              </Grid>
              <Grid item xs={2}>
                <IconButton
                  title="Delete Item"
                  id={`delete-${error}`}
                  onClick={() => removeErrorField(error)}
                  className="modifyButton"
                  size="large"
                >
                  <ClearIcon color="primary" />
                </IconButton>
              </Grid>
              <Grid item xs={2}>
                <IconButton
                  title="Edit Item"
                  id={`edit-${error}`}
                  className="modifyButton"
                  onClick={() => setEditError(error)}
                  size="large"
                >
                  <Edit color="primary" />
                </IconButton>
              </Grid>
            </Grid>
          </Box>
        );
      })}
      <Grid container spacing={2}>
        <Grid item xs={7}>
          <Button
            fullWidth
            id="deleteAllErrors"
            variant="contained"
            color="secondary"
            onClick={() => removeAllErrorFields(errorFields)}
          >
            Delete Error Variables
          </Button>
        </Grid>
      </Grid>
    </Box>
  );
  const customReportButtons = (
    <Grid container spacing={2}>
      {customVariant && (
        <Grid item md={6} xs={12}>
          <Button
            fullWidth
            id="cancelSave"
            style={{ marginBottom: 0 }}
            variant="contained"
            color="secondary"
            onClick={() => {
              returnPath && history.push(returnPath.split("?")[0]);
            }}
          >
            Cancel
          </Button>
        </Grid>
      )}
      {getCookiebyName("uuid") && (
        <Grid item md={6} xs={12}>
          <Button
            fullWidth
            id="saveReport"
            style={{ marginBottom: 0 }}
            disabled={
              selectedCheckboxes.length === 0 ||
              errorFields.length > 0 ||
              banner === "true" ||
              displayDuplicateNameError
            }
            variant="contained"
            onClick={() => {
              customVariant ? saveReportFetch() : setOpenSaveModal(true);
              returnPath && customReportName && history.push(returnPath);
            }}
            color={customVariant ? "primary" : "secondary"}
          >
            Save Report
          </Button>
        </Grid>
      )}
      {!customVariant && (
        <Grid item md={getCookiebyName("uuid") ? 6 : 12} xs={12}>
          <Button
            fullWidth
            id="generateReport"
            disabled={selectedCheckboxes.length === 0 || errorFields.length > 0}
            variant="contained"
            color="primary"
            style={{ marginBottom: 0 }}
            onClick={() => {
              setLoadVariables(true);
            }}
          >
            Generate Report
          </Button>
        </Grid>
      )}
    </Grid>
  );

  const selectedDisplay = (
    <>
      <Typography variant="h3">Selected Variables</Typography>
      <Button
        fullWidth
        id="manuallyEdit"
        variant="contained"
        color="secondary"
        onClick={() => {
          setEditMany(true);
          setSelectedValues(selectedCheckboxes.join(`\n`));
        }}
      >
        Manually Edit
      </Button>
      {errorFieldDisplay}
      <Typography
        id="list-var"
        style={
          selectedCheckboxes.length > 0
            ? { marginBottom: "1rem", wordBreak: "break-all" }
            : {}
        }
      >
        {selectedCheckboxes
          .filter(
            (item: string) =>
              item &&
              !ignoredVariables.includes(item) &&
              Object.keys(checkboxState).includes(item),
          )
          .join(",")}
      </Typography>
    </>
  );

  React.useEffect(() => {
    if (loadVariables) {
      setLoadVariables(false);
      // filters through and removes unnecessary values
      updateGenerateVars(filteredVariables);
      updateReportType([
        { ...generateSaveStructure[0], structureID: "customResults" },
      ]);
    }
  }, [
    filteredVariables,
    generateSaveStructure,
    loadVariables,
    updateGenerateVars,
    updateReportType,
  ]);
  const handleValuesUpdate = (values: any) => {
    setSelectedValues(values);
  };
  const saveEditMany = () => {
    const editManyObj: KeyValueObject[] = [];
    selectedValues.split(`\n`).forEach((variable: string) => {
      editManyObj.push({
        key: variable.toUpperCase().replace(/ /g, ""),
        value: true,
      });
    });
    setSubfields(editManyObj);
    setEditMany(false);
    setRemoveValues(true);
  };

  const editManyDisplay = (
    <>
      <Typography variant="h2">Manually Add Variables</Typography>
      <Typography variant="body2">
        Enter your variables one per line:
      </Typography>
      <TextField
        fullWidth
        multiline
        id="manuallyAddTextArea"
        className={classes.textArea}
        minRows={15}
        variant="outlined"
        aria-label="manually add textarea"
        value={selectedValues}
        onChange={(e: any) => setSelectedValues(e.target.value)}
      />
      <Grid container spacing={2} className={classes.manuallyEditButtons}>
        <Grid item md={6} xs={12}>
          <Button
            fullWidth
            variant="contained"
            color="secondary"
            onClick={() => {
              setEditMany(false);
            }}
          >
            Cancel
          </Button>
        </Grid>
        <Grid item md={6} xs={12}>
          <Button
            id="saveManuallyEdit"
            fullWidth
            variant="contained"
            color="secondary"
            type="submit"
            onClick={saveEditMany}
          >
            Save Edits
          </Button>
        </Grid>
        <Grid item xs={12}>
          <LookupVariable
            appState={appState}
            additionalFilters={"REPORT_TYPE:TRUE"}
            title="Dictionary of Report Variables"
            text={
              " Use this dictionary to manually select variables by browsing through their definitions. You can also use it to correct or delete any unrecognized variable errors. You must correct all variable errors before you can run or save a customized report."
            }
            appDisplay="3"
            onValuesUpdate={handleValuesUpdate}
            selectedVariables={selectedCheckboxes}
          />
        </Grid>
      </Grid>
    </>
  );

  function reportReturn(reportData: KeyValue | undefined) {
    if (reportData) {
      return (
        <>
          {JSON.stringify(incomeBasis) !==
          JSON.stringify(appState.ui.incomeBasis) ? (
            undefined
          ) : (
            <>
              <Grid container spacing={2} style={{ marginTop: "1rem" }}>
                {!editMany && (
                  <>
                    <Grid item xs={9}>
                      {customVariant && modalInputField}
                    </Grid>
                    <Grid item xs={3}>
                      {customReportButtons}
                    </Grid>
                  </>
                )}
                <Grid item xs={9}>
                  <Typography variant="h3">
                    Select Variables To Build A Custom Report
                  </Typography>
                  <TreeView disableSelection>
                    {reportStructure.map((report: KeyValue, int) => {
                      const reports = [
                        {
                          label: report.label,
                          variable_ytd: "",
                          variable_ytdr: "",
                          variable_qtly: "",
                          variable_qtlyr: "",
                          sub_fields: [...report.fields],
                        },
                      ];
                      return reports.map((structure: KeyValue, num: number) => (
                        <RenderCustomTree
                          key={num}
                          appState={appState}
                          structure={structure}
                          int={int}
                          setSubfields={setSubfields}
                          checkboxState={checkboxState}
                          disable={editMany}
                          data={data}
                          selectedCheckboxes={selectedCheckboxes}
                          setSelectedCheckboxes={setSelectedCheckboxes}
                          setCheckboxState={setCheckboxState}
                          ignoredVariables={ignoredVariables}
                        />
                      ));
                    })}
                  </TreeView>
                </Grid>
                <Grid item xs={3}>
                  {editMany ? editManyDisplay : selectedDisplay}
                </Grid>
              </Grid>
            </>
          )}
        </>
      );
    } else {
      return (
        <Box marginTop={3} textAlign="center" id="no-results-message">
          <Typography variant="h2" style={{ paddingBottom: 10 }}>
            No Results
          </Typography>
          <Typography
            variant="body2"
            color="primary"
            style={{ paddingBottom: 0 }}
          >
            No data is available for this Institution at the time frame
            specified, please select a different reporting period.
          </Typography>
          <Typography variant="body2" color="primary">
            You can also review the Institution’s Details for history, or
            research with Events & Changes, to find out more.
          </Typography>
        </Box>
      );
    }
  }

  return reportStructure &&
    reportStructure.length > 1 &&
    JSON.stringify(reportStructure) === JSON.stringify(previousStructure) &&
    (appState.ui.reportType === "custom" || customVariant) ? (
    <>
      {!customVariant && saveModal}
      {reportReturn(data)}
    </>
  ) : (
    <LoadingIndicator
      spinnerClass={classes.loadingSpinner}
      textClass={classes.loadingSpinner}
      spinnerSize={50}
      text="Loading"
    />
  );
}
